/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import AccordionSectionPanel from "@insite/mobius/AccordionSection/AccordionSectionPanel";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { HorizontalRule } from "@ucs/Components/HorizontalRule";
import signOut from "@ucs/Handlers/UcsSignOut";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth", 
    nodeId = "nodeId",
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
    currentPageLink: getCurrentPage(state),
});

const mapDispatchToProps = { signOut };

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NavigationListStyles {
    accordionSection: InjectableCss;
    accordionContainer: InjectableCss;
    parentContainer: any;
}

const styles: NavigationListStyles = {
    accordionContainer: {
        css: css``,
    },
    accordionSection: {
        css: css``,
    },
    parentContainer: {
        css: css`
            margin-bottom: 18px;
        `,
    },
};

interface State {
    isExpanded: boolean;
}

class MyAccountNavigationList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isExpanded: false,
        };
    }

    // This is used to return the top level menu item when menu expanded,
    // if menu is not expanded it just returns the current page.
    private getPageLinkTitle = (pageLinks: PageLinkModel[]): string | undefined => {
        const currentPageTitle = this.getCurrentPageTitle(pageLinks);

        if (this.state.isExpanded) {
            // Only return the top menu item:
            return this.getTopLevelMenuItemTitle(pageLinks);
        }
        // If not expanded we just pass back the current subpage title.
        return currentPageTitle || "";
    };

    // Gets the current sub page title. I.E. Dashboard, Account Settings, etc
    private getCurrentPageTitle = (pageLinks: PageLinkModel[]): string | undefined => {
        const nodeId = this.props.currentPageLink && this.props.currentPageLink.nodeId;
        const pageLinksCopy = pageLinks.find(e => e.id === nodeId);
        const currentPageTitle = pageLinksCopy && pageLinksCopy.title;
        return currentPageTitle || "";
    };

    // Gets top menu item title.
    private getTopLevelMenuItemTitle = (pageLinks: PageLinkModel[]): string | undefined => {
        return (pageLinks && pageLinks.length > 0 && pageLinks[0].title) || "";
    };

    // Gets top menu item nodeid to exclude it from our pageLinks array.
    private getTopLevelMenuItemNodeId = (pageLinks: PageLinkModel[]): string | undefined => {
        return (pageLinks && pageLinks.length > 0 && pageLinks[0].id) || "";
    };

    // Used to extract the first menu item from the list for the purpose of placing it in the title of the accordion container
    private getPageLinksWithoutTopMenuInList = (pageLinks: PageLinkModel[]): PageLinkModel[] => {
        return pageLinks.filter(e => e.id !== this.getTopLevelMenuItemNodeId(pageLinks));
    };

    private onSignOutHandler = () => {
        this.props.signOut();
    };

    render() {
        if (!this.props.rootPageLink || !this.props.rootPageLink.children) {
            return null;
        }
        const topLevelTitle = this.getPageLinkTitle(this.props.rootPageLink.children);

        const ListStyle = styled.ul`
            margin-left: 16px;
            margin-right: 16px;
            li {
                font-size: 18px;
                // Should change the fields for md,lg,xl screens to have their own margin setting and allow one for mobile breakpoints
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            margin-top: 16px;
                            &:last-child {
                                margin-bottom: ${this.props.fields.marginBottom || 16}px;
                            }
                        `,
                        css`
                            margin-top: 16px;
                            &:last-child {
                                margin-bottom: ${this.props.fields.marginBottom || 16}px;
                            }
                        `,
                        css`
                            margin-top: 8px;
                            margin-bottom: ${this.props.fields.marginBottom || 16}px;
                        `,
                        css`
                            margin-top: 8px;
                            margin-bottom: ${this.props.fields.marginBottom || 16}px;
                        `,
                        css`
                            margin-top: 8px;
                            margin-bottom: ${this.props.fields.marginBottom || 16}px;
                        `,
                    ]);
                }}
            }
        `;

        const renderLinks = (pageLinks: PageLinkModel[], currentDepth: number) => {
            if (currentDepth > this.props.fields.depth) {
                return;
            }
            const nextDepth = currentDepth + 1;
            const pageLinkModelClone: PageLinkModel[] & never[] = Object.assign([], pageLinks || []);

            pageLinkModelClone.push({
                title: "Log Out",
                url: "",
                type: "",
                id: "",
                parentId: "",
                excludeFromNavigation: false,
                children: undefined,
            });

            return pageLinkModelClone
                .filter(pageLink => !pageLink.excludeFromNavigation)
                .map(pageLink => {
                    const isLogout: boolean = pageLink.title === "Log Out";
                    const navItemActive = css`
                        ${!isLogout && this.props.currentPageLink.nodeId === pageLink.id ? "font-weight: 700;" : ""};
                    `;
                    return (
                        <li key={pageLink.id}>
                            {!isLogout ? (
                                <Link
                                    data-test-selector={`navigationList-${pageLink.title}`}
                                    href={pageLink.url}
                                    css={navItemActive}
                                >
                                    {pageLink.title}
                                </Link>
                            ) : (
                                <Link onClick={this.onSignOutHandler}>{pageLink.title}</Link>
                            )}

                            {/* Disabled depth as it will not be used on this specific widget yet. It is for pages under myaccount pages such as MyAccount/MyLists/MyListsDetails, depth feature doesn't
                            seem to work anyways but when adding my logout item to the list of PageModels and setting depth to 2 Logout gets added under many pages with children set to a value */}
                            {/* {pageLink.children && pageLink.children.length > 0 && (
                                <ListStyle key={pageLink.id}>
                                    {renderLinks(pageLink.children, nextDepth)}
                                </ListStyle>
                            )} */}
                        </li>
                    );
                });
        };

        return (
            <StyledWrapper {...styles.parentContainer}>
                <Hidden below="md">
                    <ListStyle data-test-selector="navigationList">
                        {renderLinks(this.props.rootPageLink.children, 1)}
                    </ListStyle>
                </Hidden>
                <Hidden above="sm">
                    <HorizontalRule
                        styles={{
                            marginRight: "16px",
                            marginLeft: "16px",
                            width: "auto",
                            borderColor: UCSColors.gray08,
                        }}
                    />
                    <Accordion {...styles.accordionContainer} headingLevel={1}>
                        <ManagedAccordionSection
                            onTogglePanel={expanded => {
                                this.setState({ isExpanded: expanded });
                            }}
                            headerProps={{
                                css: css`
                        margin-left: 16px;
                        margin-right: 16px;
                        ${({ theme }) => {
                            return breakpointMediaQueries(theme, [
                                css`
                                    padding-top: 16px;
                                    ${!this.state.isExpanded ? "padding-bottom: 16px;" : ""}
                                `,
                                css`
                                    padding-top: 16px;
                                    ${!this.state.isExpanded ? "padding-bottom: 16px;" : ""}
                                `,
                                css`
                                    padding-top: 2px;
                                    padding-bottom: 2px;
                                `,
                                css`
                                    padding-top: 2px;
                                    padding-bottom: 2px;
                                `,
                                css`
                                    padding-top: 2px;
                                    padding-bottom: 2px;
                                `,
                            ]);
                        }}
                        button > span {
                            font-size: 18px !important;
                            font-weight: normal;
                            text-transform: none !important;
                        }
                        button {
                            border: none !important;
                            height: 100%;
                            width: 100%;
                            font-family: inherit;
                            background-color: transparent !important;
                            display: flex;
                            text-align: left;
                            align-items: center;
                            justify-content: space-between;
                            cursor: pointer;
                            padding-left: 0px !important;
                            padding-right: 0px !important;
                            padding-bottom: 0px !important;
                            padding-top: 0px !important;
                            &:focus {
                                border: none !important;
                                text-decoration: none;
                                outline: none;
                                padding: 0px !important;
                                padding-left: 0px !important;
                            }
                        }
                        ${/* sc-selector */ AccordionSectionPanel} + & button {
                            border-top: none !important
                            &:focus {
                                border: none !important;
                                padding-top: 0px;
                                box-sizing: border-box;
                                
                            }
                        }
                    `,
                            }}
                            panelProps={{
                                css: css`
                                    margin: 0;
                                    border: none !important;
                                    border-top: 0;
                                    padding: 0px !important;
                                `,
                            }}
                            titleTypographyProps={{
                                css: css`
                                    ${!this.state.isExpanded ? "font-weight: 700 !important;" : ""}
                                `,
                            }}
                            title={!this.state.isExpanded ? topLevelTitle : ""}
                            initialExpanded={this.state.isExpanded}
                            {...styles.accordionSection}
                        >
                            <ListStyle data-test-selector="navigationList">
                                {renderLinks(this.props.rootPageLink.children, 1)}
                            </ListStyle>
                        </ManagedAccordionSection>
                    </Accordion>
                </Hidden>
                <Hidden above="sm">
                    <HorizontalRule
                        styles={{
                            marginRight: "16px",
                            marginLeft: "16px",
                            width: "auto",
                            borderColor: UCSColors.gray08,
                        }}
                    />
                </Hidden>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyAccountNavigationList),
    definition: {
        group: "My Account",
        icon: "NavigationList",
        fieldDefinitions: [
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
            },
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 24,
                fieldType: "General",
                max: 100,
                min: 0,
            },
        ],
    },
};

export default widgetModule;
